/**
 * src/api/vendorApi.js
 *
 * Thin client for the vendors-write Azure Function. All vendor mutations now go
 * through this module instead of the monolithic updateState() call so writes are
 * directed at the dedicated tenant_vendor_state table rather than the legacy
 * tenant_state blob.
 *
 * Every function returns the full updated vendorState payload returned by the
 * function (vendors + vendorAssessmentConfig), which callers can merge into
 * local state to keep the UI in sync without a full state reload.
 */

import { azurePost } from './azureClient';

async function callVendorsWrite(action, body, tenantId) {
  const res = await azurePost(`/api/vendors-write?action=${encodeURIComponent(action)}`, {
    ...body,
    tenantId,
  });
  return res;
}

export async function createVendor(vendor, tenantId) {
  return callVendorsWrite('create', { vendor }, tenantId);
}

export async function updateVendor(vendor, tenantId) {
  return callVendorsWrite('update', { vendor }, tenantId);
}

export async function archiveVendor(vendorId, tenantId) {
  return callVendorsWrite('archive', { id: vendorId }, tenantId);
}

export async function restoreVendor(vendorId, tenantId) {
  return callVendorsWrite('restore', { id: vendorId }, tenantId);
}

export async function deleteVendor(vendorId, tenantId) {
  return callVendorsWrite('delete', { id: vendorId }, tenantId);
}

/**
 * GH #7979: field-level patch of one or more vendors. `expected` carries, per
 * vendor id, the value each patched field held when the user loaded it; a
 * field changed by someone else meanwhile is refused with a 409 whose
 * `conflicts[]` the transport puts on `error.conflicts`.
 */
export async function bulkUpdateVendors(vendorIds, patch, expected, tenantId) {
  return callVendorsWrite('bulk-update', { vendorIds, patch, expected }, tenantId);
}

/**
 * GH #7979: the same field-level write with a different patch per vendor
 * (`patches: { [vendorId]: { field: value } }`), stored all-or-nothing in one
 * request — a bulk edit's Keep mine / Use theirs, where each vendor keeps a
 * different subset of the edit.
 */
export async function bulkPatchVendors(patches, expected, tenantId) {
  return callVendorsWrite(
    'bulk-update',
    { vendorIds: Object.keys(patches), patches, expected },
    tenantId
  );
}

export async function saveVendorAssessmentConfig(vendorAssessmentConfig, tenantId) {
  return callVendorsWrite('save-assessment-config', { vendorAssessmentConfig }, tenantId);
}

/**
 * msr 4.5a (TPS-05): set — or, with documentId null and a note, withdraw — a
 * vendor's exit plan. Appended to state.vendorExitPlans[] with server stamps;
 * returns { ok, exitPlan, stateVersion } (the transport adopts the version).
 */
export async function setVendorExitPlan(vendorId, documentId, note, tenantId) {
  return callVendorsWrite('set-exit-plan', { vendorId, documentId, note }, tenantId);
}
